import React, { useState } from "react";
import { motion } from "framer-motion";
import { Plus, HandHeart, Users, Sprout, MessageSquare } from "lucide-react";
import { LEADERSHIP, IMPACT, fadeUp } from "../../data";

const PILLAR_ICONS = [HandHeart, Users, Sprout, MessageSquare];

export const LeadershipInAction = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="relative py-24 md:py-32" style={{ background: "#FFFFFF" }}>
      <div className="container-x">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-14 max-w-2xl">
          <span className="eyebrow" style={{ color: "var(--blue)" }}>Leadership in Action</span>
          <h2 className="font-display font-bold tracking-tight text-3xl md:text-4xl lg:text-5xl mt-4" style={{ color: "var(--navy)" }}>
            Leadership is a verb.
          </h2>
          <p className="font-body text-base md:text-lg leading-relaxed mt-6" style={{ color: "var(--text2)" }}>
            Ideas are easy. Following through is the hard part. Across every industry he's worked in, the constant has been
            the same — show up, do the work, and try to leave things a little better than you found them.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-4">
          {LEADERSHIP.map((l, i) => (
            <motion.button key={l.title} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
              onClick={() => setOpen(open === i ? -1 : i)} data-testid={`leadership-card-${i}`}
              className="text-left rounded-2xl border p-6 bg-white hover:shadow-[0_14px_34px_rgba(25,118,210,0.12)] hover:border-[color:var(--blue)]/30 transition-all"
              style={{ borderColor: "var(--grey)" }}>
              <div className="flex items-center justify-between gap-4">
                <h3 className="font-display font-semibold text-lg" style={{ color: "var(--navy)" }}>{l.title}</h3>
                <motion.span animate={{ rotate: open === i ? 45 : 0 }} className="shrink-0">
                  <Plus size={20} color="var(--blue)" />
                </motion.span>
              </div>
              <motion.div initial={false} animate={{ height: open === i ? "auto" : 0, opacity: open === i ? 1 : 0 }}
                className="overflow-hidden">
                <p className="font-body text-sm leading-relaxed pt-3" style={{ color: "var(--text2)" }}>{l.body}</p>
              </motion.div>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
};

export const CreatingImpact = () => {
  return (
    <section className="relative py-24 md:py-32 overflow-hidden" style={{ background: "var(--midnight)" }}>
      {/* decorative animated Karnataka-inspired outline */}
      <svg className="absolute right-[-6%] top-1/2 -translate-y-1/2 w-[70%] max-w-3xl opacity-[0.12] pointer-events-none"
        viewBox="0 0 300 400" fill="none" aria-hidden>
        <motion.path
          d="M150 20 C110 40 95 70 100 110 C60 120 50 160 75 185 C55 215 70 250 100 255 C95 300 120 330 150 340 C180 360 200 340 205 300 C240 290 250 250 225 225 C250 195 235 155 205 150 C215 110 195 70 165 65 C170 40 165 25 150 20 Z"
          stroke="var(--gold)" strokeWidth="2" fill="none"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }}
          transition={{ duration: 2.5, ease: "easeInOut" }} />
      </svg>

      <div className="container-x relative z-10">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-14 max-w-2xl">
          <span className="eyebrow" style={{ color: "var(--gold)" }}>Creating Impact Through Connection</span>
          <h2 className="font-display font-bold tracking-tight text-3xl md:text-4xl lg:text-5xl mt-4 text-ivory">
            Small actions, added up over time.
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-6 max-w-3xl">
          {IMPACT.map((p, i) => {
            const Icon = PILLAR_ICONS[i];
            return (
              <motion.div key={p.title} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                data-testid={`impact-pillar-${i}`}
                className="glass-dark rounded-2xl p-7 hover:-translate-y-1.5 transition-transform duration-300">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl mb-4"
                  style={{ background: "rgba(25,118,210,0.18)" }}>
                  <Icon size={20} color="#5FA8F5" />
                </span>
                <h3 className="font-display font-semibold text-lg text-ivory">{p.title}</h3>
                <p className="font-body text-ivory/85 text-sm leading-relaxed mt-2">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
